import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import "leaflet.heat";
import { api } from "@/lib/api";
import { safeInvalidateSize, safeFitBounds } from "@/lib/leafletSafe";
import { labelFor, CATEGORIES } from "@/lib/categories";
import { useLang } from "@/context/LanguageContext";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";
import { MapPin, PackageSearch, Building2, LocateFixed, Search, X, Maximize2, Minimize2, Flame, BarChart3, ChevronDown, PackageCheck } from "lucide-react";
import { PageLoader } from "@/components/FindoSpinner";

// Custom colored pin markers (avoids the broken default-icon path issue).
function pin(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });
}
const AD_ICON = pin("#e11d48");
const FOUND_AD_ICON = pin("#0d9488");
const PARTNER_ICON = pin("#1e3a5f");
const ME_ICON = pin("#0d9488");
const STAT_COLORS = ["#e11d48", "#1e3a5f", "#0d9488", "#f97316", "#7c3aed", "#0284c7", "#ca8a04", "#059669"];

function CountUp({ value, className, ...rest }) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    const target = Number(value) || 0;
    if (target <= 0) { setN(0); return; }
    let raf, start;
    const dur = 900;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      setN(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className} {...rest}>{n}</span>;
}

function filterData(data, { catFilter, cityQuery, periodDays, adTypeFilter }) {
  const cq = (cityQuery || "").trim().toLowerCase();
  const cutoff = periodDays === "all" ? 0 : Date.now() - Number(periodDays) * 86400000;
  const okPeriod = (a) => periodDays === "all" || (a.created_at && new Date(a.created_at).getTime() >= cutoff);
  const okType = (a) => !adTypeFilter || adTypeFilter === "all"
    || (adTypeFilter === "found" ? a.ad_type === "found" : a.ad_type !== "found");
  const ads = data.ads.filter((a) =>
    (catFilter === "all" || a.category === catFilter) &&
    (!cq || (a.lost_place || "").toLowerCase().includes(cq) || (a.country || "").toLowerCase().includes(cq)) &&
    okType(a) && okPeriod(a));
  const partners = data.partners.filter((p) =>
    !cq || (p.city || "").toLowerCase().includes(cq) || (p.country || "").toLowerCase().includes(cq));
  return { ads, partners };
}

export default function Carte() {
  const { t } = useLang();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);
  const heatRef = useRef(null);
  const [data, setData] = useState({ ads: [], partners: [] });
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("all");
  const [searchParams] = useSearchParams();
  const [cityQuery, setCityQuery] = useState(() => searchParams.get("city") || "");
  const [cityCenter, setCityCenter] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [heatmap, setHeatmap] = useState(false);
  const [topPlaces, setTopPlaces] = useState([]);
  const [cityStats, setCityStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [periodDays, setPeriodDays] = useState("all");
  const [adTypeFilter, setAdTypeFilter] = useState(() => (["lost", "found"].includes(searchParams.get("type")) ? searchParams.get("type") : "all"));
  // A geocoded city recentres the map (showing nearby ads); otherwise the text filters markers.
  const textQuery = cityCenter ? "" : cityQuery;
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: true }).setView([46.6, 2.4], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.markerClusterGroup({
      maxClusterRadius: 50,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
    }).addTo(map);
    mapObj.current = map;

    // SPA navigation from Leaflet popups (raw HTML) — intercept internal links.
    map.on("popupopen", (e) => {
      const el = e.popup.getElement();
      if (!el) return;
      el.querySelectorAll('a[href^="/"]').forEach((a) => {
        a.addEventListener("click", (ev) => {
          ev.preventDefault();
          navigate(a.getAttribute("href"));
        });
      });
    });
    // Mini-summary tooltip when hovering a cluster (ads vs partners + cities).
    layerRef.current.on("clustermouseover", (e) => {
      const kids = e.layer.getAllChildMarkers();
      let ads = 0, partners = 0;
      const cities = new Set();
      kids.forEach((m) => {
        if (m.options.kind === "partner") partners++; else ads++;
        const pl = m.options.place;
        if (pl && !/^[A-Z]{2}$/.test(pl)) cities.add(pl);
      });
      const parts = [];
      if (ads) parts.push(`${ads} ${t("map.adsCount")}`);
      if (partners) parts.push(`${partners} ${t("map.partnersCount")}`);
      const cityList = [...cities].slice(0, 3).join(", ");
      const html = `<b>${parts.join(" · ")}</b>${cityList ? `<br/><small>${cityList}${cities.size > 3 ? "…" : ""}</small>` : ""}<br/><small style="opacity:.7">${t("pub.clickToZoom")}</small>`;
      e.layer.bindTooltip(html, { direction: "top", offset: [0, -8], className: "carte-cluster-tip" }).openTooltip();
    });
    layerRef.current.on("clustermouseout", (e) => { e.layer.closeTooltip(); });

    api("/geo/points", { auth: false })
      .then((r) => setData(r))
      .catch(() => {})
      .finally(() => setLoading(false));
    api("/geo/top-places?limit=6", { auth: false })
      .then((r) => setTopPlaces(r.places || []))
      .catch(() => {});
    api("/geo/city-stats?months=6&top=5", { auth: false })
      .then((r) => setCityStats(r))
      .catch(() => {});
    api("/geo/impact", { auth: false })
      .then((r) => setImpact(r))
      .catch(() => {});

    return () => { map.remove(); mapObj.current = null; };
  }, []);

  useEffect(() => {
    const map = mapObj.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const { ads, partners } = filterData(data, { catFilter, cityQuery: textQuery, periodDays, adTypeFilter });
    const bounds = [];
    ads.forEach((a) => {
      const isFound = a.ad_type === "found";
      const m = L.marker([a.lat, a.lng], { icon: isFound ? FOUND_AD_ICON : AD_ICON, kind: "ad", place: a.lost_place || a.country || "" }).addTo(layer);
      m.bindPopup(
        `<b>${labelFor(a.category)}</b> <span style="font-size:10px;font-weight:700;color:${isFound ? "#0d9488" : "#e11d48"}">${isFound ? "· " + t("cm.found") : "· " + t("cm.lost")}</span><br/>${(a.description || "").slice(0, 90)}<br/>` +
        `<small>${a.lost_place || ""}</small><br/>` +
        `<a href="/annonces/${a.id}">${t("pub.viewAd")}</a>`
      );
      bounds.push([a.lat, a.lng]);
    });
    partners.forEach((p) => {
      const m = L.marker([p.lat, p.lng], { icon: PARTNER_ICON, kind: "partner", place: p.city || p.country || "" }).addTo(layer);
      const eq = p.establishment_id ? `?e=${p.establishment_id}` : "";
      m.bindPopup(`<b>${p.org_name || ""}</b><br/><small>${p.city || ""} ${p.country || ""}</small><br/><a href="/partenaires/${p.id}${eq}">${t("pub.viewPartner")}</a>`);
      bounds.push([p.lat, p.lng]);
    });
    if (cityCenter) map.setView([cityCenter.lat, cityCenter.lng], 12);
    else if (bounds.length) safeFitBounds(map, bounds, { padding: [50, 50], maxZoom: 12 });
  }, [data, catFilter, textQuery, cityCenter, periodDays, adTypeFilter]);

  useEffect(() => {
    const q = cityQuery.trim();
    if (q.length < 2) { setCityCenter(null); return; }
    let alive = true;
    const id = setTimeout(() => {
      api(`/geo/city-suggest?q=${encodeURIComponent(q)}&country=`, { auth: false })
        .then((r) => { if (alive) { const c = (r.suggestions || [])[0]; setCityCenter(c ? { lat: c.lat, lng: c.lng } : null); } })
        .catch(() => { if (alive) setCityCenter(null); });
    }, 600);
    return () => { alive = false; clearTimeout(id); };
  }, [cityQuery]);

  // Heatmap layer: toggles with markers; weighted by lost-item density.
  useEffect(() => {
    const map = mapObj.current, layer = layerRef.current;
    if (!map || !layer) return;
    const { ads } = filterData(data, { catFilter, cityQuery: textQuery, periodDays, adTypeFilter });

    if (heatRef.current) { map.removeLayer(heatRef.current); heatRef.current = null; }

    if (heatmap) {
      if (map.hasLayer(layer)) map.removeLayer(layer);
      const pts = ads.map((a) => [a.lat, a.lng, 1]);
      heatRef.current = L.heatLayer(pts, {
        radius: 30, blur: 22, maxZoom: 12,
        gradient: { 0.2: "#38bdf8", 0.5: "#facc15", 0.8: "#f97316", 1: "#e11d48" },
      }).addTo(map);
    } else {
      if (!map.hasLayer(layer)) map.addLayer(layer);
    }
  }, [heatmap, data, catFilter, textQuery, periodDays, adTypeFilter]);

  const filtered = filterData(data, { catFilter, cityQuery: textQuery, periodDays, adTypeFilter });

  useEffect(() => {
    if (!mapObj.current) return;
    const id = setTimeout(() => safeInvalidateSize(mapObj.current), 250);
    return () => clearTimeout(id);
  }, [fullscreen]);

  function locateMe() {
    if (!navigator.geolocation || !mapObj.current) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        L.marker([latitude, longitude], { icon: ME_ICON }).addTo(layerRef.current)
          .bindPopup(t("hc.youAreHere")).openPopup();
        mapObj.current.setView([latitude, longitude], 13);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }

  return (
    <div className="bg-background" data-testid="carte-page">
      <div className="bg-findo-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">
            <MapPin size={14} /> {t("pub.mapWord")}
          </span>
          <h1 className="mt-4 font-display text-3xl md:text-4xl font-semibold">{t("pub.mapNearYou")}</h1>
          {(() => {
            const totalObjects = filtered.ads.length + filtered.partners.length;
            return (
              <p className="mt-2 text-white/85 text-sm md:text-base font-semibold" data-testid="carte-total-count">
                <CountUp value={totalObjects} /> {totalObjects > 1
                  ? (t("map.objectsOnMapPlural") === "map.objectsOnMapPlural" ? "objets sur la carte" : t("map.objectsOnMapPlural"))
                  : (t("map.objectsOnMapSingular") === "map.objectsOnMapSingular" ? "objet sur la carte" : t("map.objectsOnMapSingular"))}
              </p>
            );
          })()}
          {impact && impact.restituted_total > 0 && (
            <div className="mt-5 inline-flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur px-5 py-3" data-testid="carte-impact">
              <div className="h-10 w-10 rounded-full bg-findo-red grid place-items-center shrink-0">
                <PackageCheck size={20} className="text-white" />
              </div>
              <p className="text-sm text-white/90">
                <CountUp value={impact.restituted_this_month} className="font-display text-2xl font-semibold text-white" data-testid="carte-impact-count" />
                <span className="ml-2">{t("map.impactMonth")}</span>
                <span className="block text-xs text-white/50">{impact.restituted_total} {t("map.impactTotal")}</span>
              </p>
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5" data-testid="carte-legend-lost"><span className="h-3 w-3 rounded-full bg-findo-red" /> {filtered.ads.filter((a) => a.ad_type !== "found").length} {t("nav.ads").toLowerCase()}</span>
            <span className="inline-flex items-center gap-1.5" data-testid="carte-legend-found"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#0d9488" }} /> {filtered.ads.filter((a) => a.ad_type === "found").length} {t("nav.found").toLowerCase()}</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-findo-navy border border-white/40" /> {filtered.partners.length} {t("map.partnersCount")}</span>
          </div>
          {/* Filters */}
          <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="carte-filters">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                placeholder={t("map.filterCity")}
                className="h-10 w-56 rounded-full bg-white pl-9 pr-3 text-sm text-slate-700 focus:outline-none"
                data-testid="carte-filter-city" />
            </div>
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="h-10 rounded-full bg-white px-4 text-sm text-slate-700 focus:outline-none"
              data-testid="carte-filter-category">
              <option value="all">{t("map.allCategories")}</option>
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(e.target.value)}
              className="h-10 rounded-full bg-white px-4 text-sm text-slate-700 focus:outline-none"
              data-testid="carte-filter-period">
              <option value="all">{t("map.periodAll")}</option>
              <option value="7">{t("map.period7")}</option>
              <option value="30">{t("map.period30")}</option>
              <option value="90">{t("map.period90")}</option>
            </select>
            <div className="h-10 inline-flex items-center rounded-full bg-white p-1 text-sm" data-testid="carte-filter-adtype">
              {[["all", t("map.mAll")], ["lost", t("map.mLost")], ["found", t("map.mFound")]].map(([k, label]) => (
                <button key={k} onClick={() => setAdTypeFilter(k)}
                  className={`h-8 px-3 rounded-full font-semibold transition-colors ${adTypeFilter === k ? (k === "found" ? "bg-teal-600 text-white" : k === "lost" ? "bg-findo-red text-white" : "bg-findo-navy text-white") : "text-slate-600"}`}
                  data-testid={`carte-adtype-${k}`}>{label}</button>
              ))}
            </div>
            {(catFilter !== "all" || cityQuery || periodDays !== "all" || adTypeFilter !== "all") && (
              <button
                onClick={() => { setCatFilter("all"); setCityQuery(""); setPeriodDays("all"); setAdTypeFilter("all"); }}
                className="h-10 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 text-sm font-semibold text-white hover:bg-white/25 transition-colors"
                data-testid="carte-filter-clear">
                <X size={14} /> {t("map.clear")}
              </button>
            )}
          </div>
          {/* Top places */}
          {topPlaces.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="carte-top-places">
              <span className="text-xs font-semibold uppercase tracking-wide text-white/50 mr-1">{t("map.topPlaces")}</span>
              {topPlaces.map((p) => (
                <button
                  key={p.place}
                  onClick={() => setCityQuery(p.place)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                  data-testid="carte-top-place-chip">
                  <MapPin size={12} /> {p.place} <span className="text-white/60">· {p.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className="max-w-6xl mx-auto px-6 py-8 relative"
        style={fullscreen ? { position: "fixed", inset: 0, zIndex: 1000, maxWidth: "none", margin: 0, padding: 16, background: "#fff" } : undefined}
        data-testid="carte-container">
        <button onClick={locateMe} className="absolute right-10 top-14 z-[500] bg-white shadow-lg rounded-full px-4 py-2.5 text-sm font-semibold text-findo-navy inline-flex items-center gap-2 hover:bg-slate-50 transition-colors" data-testid="locate-me-btn">
          <LocateFixed size={16} /> {t("hc.locateMe")}
        </button>
        <button onClick={() => setFullscreen((v) => !v)} className="absolute right-10 top-28 z-[500] bg-white shadow-lg rounded-full px-4 py-2.5 text-sm font-semibold text-findo-navy inline-flex items-center gap-2 hover:bg-slate-50 transition-colors" data-testid="carte-fullscreen-btn">
          {fullscreen ? <><Minimize2 size={16} /> {t("map.exitFullscreen")}</> : <><Maximize2 size={16} /> {t("map.fullscreen")}</>}
        </button>
        <button onClick={() => setHeatmap((v) => !v)} className={`absolute right-10 top-[10.5rem] z-[500] shadow-lg rounded-full px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 transition-colors ${heatmap ? "bg-findo-red text-white hover:bg-findo-red/90" : "bg-white text-findo-navy hover:bg-slate-50"}`} data-testid="carte-heatmap-btn">
          <Flame size={16} /> {t("map.heatmap")}
        </button>
        <div ref={mapRef} className="w-full rounded-3xl overflow-hidden border border-border card-shadow" style={{ height: fullscreen ? "calc(100vh - 32px)" : "62vh", minHeight: 420, zIndex: 0 }} data-testid="leaflet-map" />
        {loading && <PageLoader label={t("c.loading")} className="mt-4" size={32} />}
        {!loading && !heatmap && filtered.ads.length === 0 && filtered.partners.length === 0 && (
          <p className="mt-4 text-center text-slate-400 text-sm inline-flex items-center gap-2 justify-center w-full"><PackageSearch size={16} /> {t("pub.noPointsYet")}</p>
        )}

        {/* City statistics */}
        {cityStats?.top?.length > 0 && (
          <div className="mt-6" data-testid="carte-stats">
            <button onClick={() => setShowStats((v) => !v)} className="inline-flex items-center gap-2 text-sm font-semibold text-findo-navy hover:text-findo-red transition-colors" data-testid="carte-stats-toggle">
              <BarChart3 size={16} /> {t("map.stats")}
              <ChevronDown size={15} className={`transition-transform ${showStats ? "rotate-180" : ""}`} />
            </button>
            {showStats && (
              <div className="mt-4 grid md:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl border border-border card-shadow p-5" data-testid="carte-stats-bar">
                  <p className="text-sm font-semibold text-findo-navy mb-3">{t("map.statsTop")}</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={cityStats.top} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="place" width={110} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#e11d48" radius={[0, 6, 6, 0]} name="Objets" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-2xl border border-border card-shadow p-5" data-testid="carte-stats-trend">
                  <p className="text-sm font-semibold text-findo-navy mb-3">{t("map.statsTrend")}</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={cityStats.series} margin={{ left: -20, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {(cityStats.keys || []).map((k, i) => (
                        <Line key={k} type="monotone" dataKey={k} stroke={STAT_COLORS[i % STAT_COLORS.length]} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
